import { Routes } from '@angular/router';
import { authGuard } from '@common/guards/auth.guard';
import { authorizedInvestorGuard } from '@common/guards/authorized-investor.guard';
import { bankAccountGuard } from '@common/guards/bank-account.guard';
import { kycVersionsGuard } from '@common/guards/kyc-versions.guard';
import { suitabilityDetailsResolver } from '@common/resolvers/suitability-details.resolver';
import { VotingGuard } from '@common/guards/voting.guard';
import { UpcomingProductGuard } from '@common/guards/upcoming-product.guard';

const dashboardRoutes: Routes = [
  {
    path: 'dashboard',
    loadComponent: () =>
      import('./dashboard.component').then(mod => mod.DashboardComponent),
    canActivateChild: [authGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./home/home.component').then(mod => mod.HomeComponent),
        canActivate: [authGuard],
      },
      {
        path: 'products',
        loadComponent: () =>
          import('./products/products.component').then(
            mod => mod.ProductsComponent
          ),
        canActivate: [authGuard],
      },
      {
        path: 'product/:id',
        loadComponent: () =>
          import('./product-details/product-details.component').then(
            mod => mod.ProductDetailsComponent
          ),
        canActivate: [authGuard, VotingGuard, UpcomingProductGuard],
        children: [
          {
            path: 'investment',
            loadComponent: () =>
              import('./product-details/my-investments/my-investments.component').then(
                m => m.MyInvestmentsComponent
              ),
          },
          {
            path: 'profit-loss',
            loadComponent: () =>
              import('./product-details/profit-loss/profit-loss.component').then(
                m => m.ProfitLossComponent
              ),
          },
          {
            path: 'reports',
            loadComponent: () =>
              import('./product-details/reports/reports.component').then(
                m => m.ReportsComponent
              ),
          },
          {
            path: 'brief',
            loadComponent: () =>
              import('./product-details/brief/brief.component').then(
                m => m.BriefComponent
              ),
          },
          {
            path: 'details',
            loadComponent: () =>
              import('./product-details/product-details.component').then(
                mod => mod.ProductDetailsComponent
              ),
          },
          {
            path: 'attachments',
            loadComponent: () =>
              import('./product-details/attachments/attachments.component').then(
                m => m.AttachmentsComponent
              ),
          },
          {
            path: 'voting',
            loadComponent: () =>
              import('./product-details/voting/voting.component').then(
                m => m.VotingComponent
              ),
          },
        ],
      },
      {
        path: 'product/:productId/voting-session/:votingSessionUuid',
        loadComponent: () =>
          import('./product-details/voting/voting-session/voting-session-details/voting-session-details.component').then(
            mod => mod.VotingSessionDetailsComponent
          ),
        canActivate: [authGuard],
      },
      {
        path: 'suitability-details',
        loadComponent: () =>
          import('./suitability-details/suitability-details.component').then(
            mod => mod.SuitabilityDetailsComponent
          ),
        canActivate: [authGuard],
        resolve: { suitabilityData: suitabilityDetailsResolver },
      },
      {
        path: 'my-account',
        loadComponent: () =>
          import('./account/account.component').then(
            mod => mod.AccountComponent
          ),
        canActivate: [authGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./account/my-account/my-account.component').then(
                mod => mod.MyAccountComponent
              ),
            canActivate: [authGuard],
          },
          {
            path: 'bank-account',
            loadComponent: () =>
              import('./account/bank-account/bank-account.component').then(
                mod => mod.BankAccountComponent
              ),
            canActivate: [authGuard, bankAccountGuard],
          },
          {
            path: 'authorized-investor',
            loadComponent: () =>
              import('./account/authorized-investor/authorized-investor.component').then(
                mod => mod.AuthorizedInvestorComponent
              ),
            canActivate: [authGuard, authorizedInvestorGuard],
          },
          {
            path: 'authorized-company',
            loadComponent: () =>
              import('./account/authorized-company/authorized-company.component').then(
                mod => mod.AuthorizedCompanyComponent
              ),
            canActivate: [authGuard, authorizedInvestorGuard],
          },
          {
            path: 'authorized-rejection-status',
            loadComponent: () =>
              import('./account/authorized-rejection-status/authorized-rejection-status.component').then(
                mod => mod.AuthorizedRejectionStatusComponent
              ),
            canActivate: [authGuard],
          },
          {
            path: 'kyc-history',
            loadComponent: () =>
              import('./account/kyc-versions/kyc-versions.component').then(
                mod => mod.KycVersionsComponent
              ),
            canActivate: [authGuard, kycVersionsGuard],
          },
          {
            path: 'view-kyc/:selector',
            loadComponent: () =>
              import('./account/view-kyc/view-kyc.component').then(
                mod => mod.ViewKycComponent
              ),
          },
        ],
      },
      {
        path: 'held-units',
        loadComponent: () =>
          import('./investments/held-units/held-units.component').then(
            mod => mod.HeldUnitsComponent
          ),
        canActivate: [authGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./investments/investments.component').then(
                mod => mod.InvestmentsComponent
              ),
            canActivate: [authGuard],
          },
        ],
      },
      {
        path: 'my-investments-list',
        loadComponent: () =>
          import('./investments/my-investments-list/my-investments-list.component').then(
            mod => mod.MyInvestmentsListComponent
          ),
        canActivate: [authGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./investments/investments.component').then(
                mod => mod.InvestmentsComponent
              ),
            canActivate: [authGuard],
          },
        ],
      },
      {
        path: 'investment-process',
        loadComponent: () =>
          import('./investments/investment-process/investment-process.component').then(
            mod => mod.InvestmentProcessComponent
          ),
        canActivate: [authGuard],
      },
      {
        path: 'support',
        loadComponent: () =>
          import('./support/support.component').then(
            mod => mod.SupportComponent
          ),
        canActivate: [authGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./support/main-support/main-support.component').then(
                mod => mod.MainSupportComponent
              ),
            canActivate: [authGuard],
          },
          {
            path: 'account-support',
            loadComponent: () =>
              import('./support/account-support/account-support.component').then(
                mod => mod.AccountSupportComponent
              ),
            canActivate: [authGuard],
          },
        ],
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./settings/settings.component').then(
            mod => mod.SettingsComponent
          ),
        canActivate: [authGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./settings/main-settings/main-settings.component').then(
                mod => mod.MainSettingsComponent
              ),
            canActivate: [authGuard],
          },
          {
            path: 'change-password',
            loadComponent: () =>
              import('./settings/change-password/change-password.component').then(
                mod => mod.ChangePasswordComponent
              ),
            canActivate: [authGuard],
          },
          {
            path: 'email-verification',
            loadComponent: () =>
              import('./settings/email-verification/email-verification.component').then(
                mod => mod.EmailVerificationComponent
              ),
            canActivate: [authGuard],
          },
        ],
      },
      {
        path: 'more',
        loadComponent: () =>
          import('./more/more.component').then(mod => mod.MoreComponent),
        canActivate: [authGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./more/more-list/more-list.component').then(
                mod => mod.MoreListComponent
              ),
            canActivate: [authGuard],
          },
          {
            path: 'conditions-terms',
            loadComponent: () =>
              import('./more/conditions-terms/conditions-terms.component').then(
                mod => mod.ConditionsTermsComponent
              ),
            canActivate: [authGuard],
          },
          {
            path: 'endorsements',
            loadComponent: () =>
              import('./more/endorsements/endorsements.component').then(
                mod => mod.EndorsementsComponent
              ),
            canActivate: [authGuard],
          },
          {
            path: 'privacy-policy',
            loadComponent: () =>
              import('./more/privacy-policy/privacy-policy.component').then(
                mod => mod.PrivacyPolicyComponent
              ),
            canActivate: [authGuard],
          },
          {
            path: 'faq',
            loadComponent: () =>
              import('./more/faq/faq.component').then(mod => mod.FaqComponent),
            canActivate: [authGuard],
          },
          {
            path: 'delete-account',
            loadComponent: () =>
              import('./more/delete-account/delete-account.component').then(
                mod => mod.DeleteAccountComponent
              ),
            canActivate: [authGuard],
          },
          {
            path: 'download-user-data',
            loadComponent: () =>
              import('./more/download-user-data/download-user-data.component').then(
                mod => mod.DownloadUserDataComponent
              ),
            canActivate: [authGuard],
          },
        ],
      },
      {
        path: 'about',
        loadComponent: () =>
          import('./about/about.component').then(mod => mod.AboutComponent),
        canActivate: [authGuard],
      },
    ],
  },
  {
    path: 'kyc-final-screen',
    loadComponent: () =>
      import('./account/kyc-final-screen/kyc-final-screen.component').then(
        mod => mod.KycFinalScreenComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: 'voting-success',
    loadComponent: () =>
      import('./product-details/voting/voting-success/voting-success.component')
        .then()
        .then(mod => mod.VotingSuccessComponent),
    canActivate: [authGuard],
  },
  {
    path: 'investment-blocked',
    loadComponent: () =>
      import('./product-details/investment-blocked/investment-blocked.component').then(
        mod => mod.InvestmentBlockedComponent
      ),
    canActivate: [authGuard],
  },
];

export default dashboardRoutes;
